import type { Destination as ApiDestination, Health as ApiHealth, InputStateReason, InputView, OutputStateReason, OutputView, Profile, Sample, StreamKey, UserView } from '../api';
import { wireKbps } from './wire';
import type { Destination, FillView, History, LinkDiag, PlatformBlock, OutputState, Platform, Point, Processing, Q, SourceInfo, StreamSummary } from './types';
import { summarize } from './quality';
import { srtLink } from './srt';
import { fillShare } from '../fill';
import { transcodeView } from './transcode';
import { markRow } from './watermark';
import { captionsDeliveryView } from './captionsDelivery';
import { outputCaptionsChip } from './captions';
import { outputTracks } from './tracks';
import { t } from '../i18n';
import { destVertical, mainInput, mainKey, pairView, verticalOn, type PairView } from './vertical';

/*
 * API → view models of the Live screen. Texts for people live here (decision 4
 * in docs/m15-live-ui.md): built from state_reason, never from last_error, and
 * without the platform name.
 */

export const ORIGINAL: Processing = { spoken: 'auto', audio: 'original', captions: 'off', captionsAs: 'burned', addedDelaySec: 0 };

export const PLATFORM_LABEL: Record<Platform, string> = { youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', rtmp: 'RTMP' };

export function platformOf(profile: string): Platform {
  return profile === 'youtube' || profile === 'twitch' || profile === 'kick' || profile === 'facebook' || profile === 'instagram' || profile === 'tiktok' ? profile : 'rtmp';
}

/** needs_key on a platform that issues a new key for every live (Instagram): the old one was spent. */
export const NEEDS_KEY_PER_SESSION = 'Instagram issues a new key for every live. Paste the new one.';

/** Does this profile take a new key for every live? Falls back to the profile id while profiles load. */
export function keyPerSession(profile: string, profiles?: Profile[]): boolean {
  const p = profiles?.find((x) => x.id === profile);
  return p ? !!p.key_per_session : profile === 'instagram';
}

/** Why a destination is not on air (reconnecting / failed). */
export const OUTPUT_REASON: Record<OutputStateReason, string> = {
  source_offline: 'Goes live when OBS starts.',
  needs_key: 'Add the stream key to go live.',
  dual_next_broadcast: 'YouTube adds it from the next broadcast.',
  platform_rejected_key: 'Rejected the stream key.',
  platform_rejected: 'Rejected the server address.',
  platform_unreachable: "Can't reach the server.",
  platform_tls: 'Secure connection failed.',
  platform_disconnected: 'Stopped answering.',
  platform_stalled: 'Stopped reading the stream.',
  slow_link: "Link can't keep up, frames dropped.",
  gop_too_long: 'Keyframes over 5 s apart: YouTube may drop parts. Set Keyframe Interval to 2 s in OBS.',
  lagging: 'Falling behind, delay growing.',
  node_unreachable: 'Media server unreachable, state unknown.',
  internal: 'Something went wrong on our side.',
  // E1: the row says which track (tracks.ts outputTracks); these are the fallbacks without the tracks
  track_missing: 'The selected track isn’t arriving: sending track 0.',
  track_codec_unsupported: 'The selected track’s codec can’t go to this platform.',
};

/** Input (OBS) texts for the summary health line. */
export const INPUT_REASON: Record<InputStateReason, string> = {
  source_offline: 'Waiting for OBS',
  key_revoked: 'Stream key revoked. Issue a new one in OBS settings',
  stream_suspended: 'Suspended by an administrator',
  account_blocked: 'Account blocked',
  codec_unsupported: 'Unsupported codec. Set OBS to H.264 and AAC',
  bitrate_over_limit: 'Bitrate over your limit. Lower it in OBS',
  unstable: 'Unstable signal from OBS',
  node_unreachable: 'Media server unreachable, state unknown',
  // plan refusals (BILL-B5a): the full text with what to do is the tip (billing.ts refusalText)
  plan_limit_concurrent: 'Refused: broadcasts at a time on your plan',
  plan_limit_stream_hours: 'Refused: hours on air used up',
  plan_paused: 'Refused: plan paused',
  plan_payment_disputed: 'Refused: payment disputed',
  account_frozen: 'Refused: account frozen',
  account_streams_blocked: 'Refused: broadcasts blocked',
  plan_no_vertical: 'Vertical stream not on your plan',
  ingest_mismatch: 'Refused: another stream is on a different ingest server',
};

const HEALTH_TEXT: Record<ApiHealth, string> = { good: 'Steady', warning: 'Needs attention', bad: 'Problem', offline: 'Offline' };
const QOF: Record<ApiHealth, Q> = { good: 'good', warning: 'warn', bad: 'bad', offline: 'idle' };

/** Codes whose text is ours and complete: shown without "{Platform} says:". */
const OWN_ISSUES = new Set(['stream_not_found', 'other_channel']);

/** The "On {Platform}" block by docs/m15-live-stats-ui-handoff.md 3 (first matching row wins). */
export function platformBlock(o: OutputView, platform: Platform, live: boolean): PlatformBlock | undefined {
  const pv = o.platform;
  if (!pv || platform === 'rtmp') return undefined;
  const label = PLATFORM_LABEL[platform];
  const cells: PlatformBlock['cells'] = platform === 'youtube' ? ['likes'] : platform === 'twitch' ? ['uptime', 'followers'] : [];
  const base = { label, cells, issues: [] as PlatformBlock['issues'] };
  const connect = platform === 'youtube' || platform === 'twitch' ? platform : undefined;
  if (pv.link === 'unsupported' || (pv.link === 'not_connected' && !connect)) {
    return { ...base, state: 'unsupported', text: platform === 'instagram' ? "Instagram doesn't share live stats with other apps." : `Live stats for ${label} are coming later.` };
  }
  if (pv.link === 'not_connected') return { ...base, state: 'connect', connect };
  const issues = (pv.issues ?? []).map((i) => ({ text: OWN_ISSUES.has(i.code) ? i.text : `${label} says: ${i.text}`, severity: i.severity }));
  const stats = pv.stats ? { viewers: pv.stats.viewers, likes: pv.stats.likes, newFollowers: pv.stats.new_followers, uptimeS: pv.stats.uptime_s } : undefined;
  const updatedAt = pv.updated_at ? Date.parse(pv.updated_at) : undefined;
  const offline = { ...base, state: 'offline' as const, text: 'Numbers show up when this destination is live.' };
  if (!live) return offline;
  if (pv.stale) return { ...base, state: 'stale', stats, updatedAt, issues };
  if (issues.length) return { ...base, state: 'issue', stats, updatedAt, issues };
  if (stats) return { ...base, state: 'ok', stats, updatedAt, issues };
  return offline;
}

export function cardState(o: OutputView): OutputState {
  switch (o.state) {
    case 'off':
    case 'stopped':
      return 'stopped';
    case 'pending':
    case 'starting':
      return 'starting';
    case 'live':
    case 'waiting':
    case 'reconnecting':
    case 'failed':
    case 'stopping':
    case 'unknown':
      return o.state;
    default:
      return 'unknown';
  }
}

/** One window of a destination: live → bandwidth coloured by health; trying → a red drop; off → no data. */
export function outputPoint(s: Sample): Point {
  if (s.state === 'live') return { v: s.bitrate_kbps / 1000, q: QOF[s.health] === 'idle' ? 'bad' : QOF[s.health] };
  if (s.state === 'reconnecting' || s.state === 'failed' || s.state === 'starting') return { v: Math.max(0, s.bitrate_kbps / 1000), q: 'bad' };
  return { v: -1, q: 'idle' };
}

/** The 5-minute window: HISTORY_POINTS samples of 5 s. */
export const HISTORY_POINTS = 60;

/**
 * The center keeps the history of an output or input only while it is active and 5 minutes
 * after (SSE-CAP): before, every destination got a "no stream" sample every 5 s. The chart
 * draws the rest of the window as "no stream", as those samples did. Idle points (v −1)
 * count nowhere: not in stats, dips or "no data yet".
 */
export function padIdle(h: History | undefined): History {
  const pts = h ?? [];
  if (pts.length >= HISTORY_POINTS) return pts;
  return [...Array.from({ length: HISTORY_POINTS - pts.length }, (): Point => ({ v: -1, q: 'idle' })), ...pts];
}

export function inputPoint(s: Sample): Point {
  if (s.health === 'offline' || (s.state && s.state !== 'live')) return { v: -1, q: 'idle' };
  return { v: wireKbps(s) / 1000, q: QOF[s.health] };
}

const secondsSince = (iso: string | undefined, now: number) => (iso ? Math.max(0, (now - Date.parse(iso)) / 1000) : undefined);

export interface LiveModel {
  summary: StreamSummary;
  source: SourceInfo;
  input?: InputView;
  dests: Destination[];
  attention: number;
  onAir: number;
  nodeDown: boolean;
  /** VERT-1: the vertical input next to the main one (turned on, or on air); absent = no vertical */
  vertical?: PairView;
}

/**
 * pending: destination id → wanted `enabled` after a click, until the server reflects it.
 * at: when the view's counters were last updated (ms).
 */
export function buildModel(view: UserView, opts: {
  pending: Record<number, boolean>; now: number; at: number; saved?: ApiDestination[]; profiles?: Profile[]; fill?: boolean;
  /** VERT-1: GET /api/stream-keys (the vertical input of the main key) */
  keys?: StreamKey[];
}): LiveModel {
  const { pending, now, at } = opts;
  const nodeDown = view.node === 'unreachable';
  // Decision 3: the sender decides. The live input is the broadcast; else the first key. The vertical half of a
  // pair (VERT-1) is never "the input": it follows its main stream.
  const input = mainInput(view);
  const unknownInput = nodeDown || input?.state_reason === 'node_unreachable';
  const sending = !!input?.live;

  const srcHist: History = input ? padIdle(view.history?.inputs[input.stream_name]?.map(inputPoint)) : [];
  const fps = input?.fps ? Math.round(input.fps) : undefined;
  const encoder = input?.live
    ? [input.video_profile && `H.264 ${input.video_profile}`, input.resolution && (fps ? `${input.resolution} ${fps} fps` : input.resolution),
        input.gop_seconds ? `keyframe ${input.gop_seconds.toFixed(1)} s` : '', input.audio_format].filter(Boolean).join(' · ')
    : 'Not sending yet';

  const source: SourceInfo = {
    app: input?.encoder && /obs/i.test(input.encoder) ? 'OBS Studio' : 'Your encoder',
    streamName: input?.stream_name,
    sending,
    unknown: unknownInput,
    // What the encoder sends, filler included: the number to compare with OBS's stats (DIAG-BITRATE-1).
    uploadMbps: sending && !unknownInput ? wireKbps(input!) / 1000 : undefined,
    jitterMs: sending && !unknownInput && input?.jitter_ms !== undefined ? Math.round(input.jitter_ms) : undefined,
    reconnects: input?.reconnects ?? 0,
    received: input?.bytes_received !== undefined ? { bytes: input.bytes_received, at, rateMbps: input.bitrate_kbps / 1000, live: sending && !unknownInput } : undefined,
    history: srcHist,
    summary: unknownInput
      ? { text: '? Media server unreachable, state unknown', tone: 'mute' }
      : sending ? summarize(srcHist, 'live', secondsSince(input?.since, now)) : { text: '○ No stream yet', tone: 'mute' },
    encoder,
    delay: sending && !unknownInput && input!.delay
      ? { toUsMs: input!.delay.to_us_ms, rttMs: input!.delay.rtt_ms, backlogMs: input!.delay.backlog_ms }
      : undefined,
    preview: sending && !unknownInput && input!.preview_available !== undefined ? { stream: input!.stream_name, available: input!.preview_available } : undefined,
    node: input?.node ? { name: input.node.name, region: input.node.region, ...(input.node.group ? { group: input.node.group } : {}) } : undefined,
    protocol: input?.protocol,
    srt: sending && !unknownInput && input?.protocol === 'srt' && input.network?.srt ? srtLink(input.network.srt) : undefined,
    slate: input?.slate && !unknownInput ? { reason: input.slate.reason, until: input.slate.until } : undefined,
  };

  const reason = input?.state_reason;
  const summary: StreamSummary = {
    live: sending,
    since: input?.since,
    bitrateMbps: sending && !unknownInput ? wireKbps(input!) / 1000 : undefined,
    fps: sending && !unknownInput && input!.fps ? input!.fps : undefined,
    keyframeSec: sending && !unknownInput && input!.gop_seconds ? input!.gop_seconds : undefined,
    resolution: sending ? input?.resolution : undefined,
    health: unknownInput ? 'offline' : input?.health ?? 'offline',
    healthText: unknownInput ? INPUT_REASON.node_unreachable : reason ? INPUT_REASON[reason] : input ? HEALTH_TEXT[input.health] : 'Waiting for OBS',
  };

  const pair = pairView(view, input, opts.keys, opts.saved, opts.profiles, nodeDown);
  const keyOn = opts.keys ? verticalOn(mainKey(opts.keys, input?.stream_name)) : undefined;
  const dests: Destination[] = view.outputs.map((o) => {
    let state = cardState(o);
    const wanted = pending[o.destination_id];
    const clicked = wanted !== undefined && wanted !== o.enabled;
    if (clicked) state = wanted ? 'starting' : 'stopping';
    const r = o.state_reason;
    const issue = state === 'live' && (r === 'slow_link' || r === 'lagging') ? r : undefined;
    // gop_too_long (HLS outputs): live, the platform may drop it; shown as the note, not an issue badge
    const retryAt = o.next_retry_at ? Date.parse(o.next_retry_at) : undefined;
    const left = retryAt !== undefined ? Math.max(0, Math.ceil((retryAt - now) / 1000)) : undefined;
    const row = opts.saved?.find((d) => d.id === o.destination_id);
    const profile = row?.profile ?? o.profile;
    const needsKey = state === 'waiting' && r === 'needs_key';
    let note: string | undefined;
    switch (state) {
      case 'live':
        note = issue ? OUTPUT_REASON[issue] : r === 'gop_too_long' ? t('row.gopNote') : undefined;
        break;
      case 'waiting':
        note = needsKey ? (keyPerSession(profile, opts.profiles) ? NEEDS_KEY_PER_SESSION : OUTPUT_REASON.needs_key) : 'Goes live when OBS starts.';
        break;
      case 'stopped':
        note = 'Off. Turn on any time.';
        break;
      case 'starting':
        note = 'Reaching the server…';
        break;
      case 'stopping':
        note = 'Closing the session…';
        break;
      case 'unknown':
        note = OUTPUT_REASON.node_unreachable;
        break;
      case 'reconnecting':
      case 'failed': {
        const why = r ? OUTPUT_REASON[r] : 'Not connected.';
        const when = left !== undefined && left > 0 ? (state === 'failed' ? ` Next try in ${left} s.` : ` Retrying in ${left} s.`) : state === 'reconnecting' ? ' Retrying now…' : '';
        note = why + when;
        break;
      }
    }
    const hist: History = padIdle(view.history?.outputs[o.output_id]?.map(outputPoint));
    const live = state === 'live';
    // E1: the selected tracks and why the output sends another one (state_reason track_*)
    const tracks = nodeDown ? undefined : outputTracks(row, r, o.state, PLATFORM_LABEL[platformOf(profile)], input?.tracks);
    if (tracks?.note) note = tracks.note.text;
    // VERT-1: the vertical input of this output and what it means now (mockup A, F; VT-17) — the center's
    // input_effective (VERT-LIVE: dual_next_broadcast, vertical_lost_at), nothing computed here
    const vertical = nodeDown ? undefined : destVertical(o, row, { profiles: opts.profiles, pair, nodeDown, keyOn });
    if (vertical?.state === 'off' && state !== 'live') note = t('vert.out.offVertical');
    if (vertical?.state === 'next') note = t('vert.f.ytLate.b');
    return {
      id: o.destination_id,
      outputId: o.output_id,
      name: o.name,
      profile,
      platform: platformOf(profile),
      profileName: PLATFORM_LABEL[platformOf(profile)],
      protocol: row?.protocol,
      enabled: clicked ? wanted! : o.enabled,
      apiState: o.state,
      state,
      issue,
      bitrateMbps: live && !nodeDown ? o.bitrate_kbps / 1000 : undefined,
      lagSec: live && !nodeDown ? o.lag_ms / 1000 : undefined,
      reconnects: o.reconnects,
      retryAt: left !== undefined && left > 0 ? retryAt : undefined,
      sent: o.bytes_sent !== undefined && state !== 'stopped' ? { bytes: o.bytes_sent, at, rateMbps: o.bitrate_kbps / 1000, live } : undefined,
      note,
      portraitLandscape: live && !nodeDown && !!o.input_effective?.landscape,
      reason: r,
      needsKeyFix: r === 'platform_rejected_key' || r === 'platform_rejected',
      needsKey,
      busy: clicked || o.state === 'pending' || o.state === 'stopping',
      history: hist,
      summary: summarize(hist, state, secondsSince(o.state_since, now)),
      processing: ORIGINAL,
      captions: nodeDown ? undefined : outputCaptionsChip(o.captions),
      // one broadcast at a time (decision 3): its live outputs carry the break screen
      slate: !nodeDown && live && !!input?.slate,
      chat: o.chat ? { access: o.chat.access, state: o.chat.state, reason: o.chat.reason } : undefined,
      tracks,
      vertical,
      input: row?.input ?? o.input,
      fallback: row?.fallback,
      inputEffective: o.input_effective ?? row?.input_effective,
      dualOf: row?.dual_of ?? undefined,
      platformBlock: platformBlock(o, platformOf(profile), live && !nodeDown),
      viewers: live && !nodeDown ? o.platform?.stats?.viewers : undefined,
      fill: opts.fill ? fillView(o, row, live && !nodeDown) : undefined,
      transcode: transcodeView(row?.transcode_effective, PLATFORM_LABEL[platformOf(profile)]),
      // WM3: only when the center burns the mark in on this output (WMU-03): no GPU, no line and no warning
      mark: markRow(row?.transcode_effective),
      // the destinations API answers from the current settings (a change shows at once); the live view between reads
      captionsDelivery: captionsDeliveryView(row?.captions_delivery_effective ?? o.captions_delivery_effective, PLATFORM_LABEL[platformOf(profile)], profile),
      delay: o.delay
        ? {
            kind: o.delay.kind,
            totalMs: o.delay.kind === 'none' ? undefined : o.delay.total_ms,
            toPlatformMs: o.delay.to_platform_ms,
            platformToViewersMs: o.delay.platform_to_viewers_ms,
            latencyMode: o.delay.latency_mode,
            warning: o.delay.warning,
            aboveMedian: o.delay.above_median,
            toUsMs: input?.delay?.to_us_ms,
            rttMs: input?.delay?.rtt_ms,
            link: live && !nodeDown ? linkDiag(o) : undefined,
          }
        : undefined,
    };
  });

  if (view.viewers_total !== undefined) {
    summary.viewers = {
      total: view.viewers_total,
      delta: view.viewers_delta_5m,
      parts: dests.filter((d) => d.viewers !== undefined).map((d) => ({ name: d.name, viewers: d.viewers! })),
      // distinct platforms with numbers: two YouTube destinations are one platform
      platforms: new Set(dests.filter((d) => d.viewers !== undefined).map((d) => d.platform)).size,
    };
  }

  return {
    summary,
    source,
    input,
    dests,
    attention: dests.filter((d) => d.state === 'reconnecting' || d.state === 'failed' || d.issue || d.needsKeyFix).length,
    onAir: dests.filter((d) => d.state === 'live').length,
    nodeDown,
    vertical: pair,
  };
}

/**
 * The fill row of a card: live — the node's mode, bitrate_kbps (what goes to the platform, filler
 * included) and fill_kbps; otherwise the mode the center resolved for the destination.
 */
export function fillView(o: OutputView, saved: ApiDestination | undefined, live: boolean): FillView {
  const eff = saved?.fill_effective;
  const base: FillView = { mode: eff?.mode, quality: eff?.quality };
  if (!live) return base;
  const added = Math.max(0, o.fill_kbps ?? 0);
  return {
    mode: o.fill_mode ?? eff?.mode,
    quality: eff?.quality,
    totalMbps: o.bitrate_kbps / 1000,
    addedMbps: added / 1000,
    share: fillShare(added, o.bitrate_kbps),
    suspended: !!o.fill_suspended,
  };
}

/** Diagnostics of the us → platform link; undefined when the node reports none. */
export function linkDiag(o: OutputView): LinkDiag | undefined {
  const n = o.network;
  const d: LinkDiag = {
    rttMs: n?.rtt_ms,
    retransPct: n?.retrans_pct,
    ackDelayMs: o.ack_delay_ms || undefined,
    sendQueueMs: o.send_queue_ms,
    cc: n?.cc,
  };
  return Object.values(d).some((v) => v !== undefined) ? d : undefined;
}

/** Byte counter now: grows by the last bitrate between samples, never goes back. */
export function counterMB(c: { bytes: number; at: number; rateMbps?: number; live: boolean } | undefined, now: number): number | undefined {
  if (!c) return undefined;
  const grow = c.live && c.rateMbps && c.at ? (c.rateMbps / 8) * Math.min(10, Math.max(0, (now - c.at) / 1000)) : 0;
  return c.bytes / 1e6 + grow;
}
